import React from 'react';
import { useDispatch } from 'react-redux';
import RoleService from '../Services/roleService';
import { navbutton_click, submenubutton_click } from '../redux/actions';
import { iconsEnabled } from '../Helpers/iconHelper';

// The pinned / expanded sidebar body (256px labelled menu). Renders the same
// role-filtered menu tree as the icon rail, as inline rows + expandable groups,
// with a full-tree search filter, a brand row (collapse) and a footer (profile +
// collapse). Navigation mirrors NavFlyout: navbutton_click then submenubutton_click.
export default function ExpandedNav({
  visibleSections,
  activeButtons,
  openGroup,
  setOpenGroup,
  query,
  setQuery,
  onNavigate,
  onCollapse,
  goHome,
  loggedInUser,
  profileActive,
  onProfileToggle,
}) {
  const dispatch = useDispatch();

  const label = (m) => (m.linktext ? m.linktext : m.text);

  // Sub-items with their ORIGINAL menuitems index preserved (activeButtons.sub and
  // submenubutton_click both use the un-filtered index), then role-filtered.
  const permittedSubs = (section) =>
    (section.submenu || [])
      .map((sub, i) => ({ sub, i }))
      .filter(({ sub }) => RoleService.CheckUserPermission('submenu', sub.key));

  const q = query.trim().toLowerCase();
  const matches = (m) => (m.text || '').toLowerCase().includes(q) || (m.linktext || '').toLowerCase().includes(q);

  const goSection = (section, index) => {
    dispatch(navbutton_click(index, section.link, section.text, section.linktext, section.icon, section.url));
    onNavigate();
  };

  const goSub = (section, index, sub, subIndex) => {
    dispatch(navbutton_click(index, section.link, section.text, section.linktext, section.icon, section.url));
    dispatch(submenubutton_click(subIndex, sub.link, sub.linktext ? sub.linktext : sub.text, sub.icon, sub.url, index));
    onNavigate();
  };

  // Clicking the group's icon/label always opens it (never collapses) and navigates
  // to its first permitted sub-item — collapsing is the arrow's job (toggle only, no
  // nav). This lets a click on an already-open group still navigate rather than close.
  const openAndNavigate = (section, index) => {
    setOpenGroup(index);
    const subs = permittedSubs(section);
    if (subs.length > 0) goSub(section, index, subs[0].sub, subs[0].i);
  };

  // Build the (optionally filtered) row model.
  const rows = [];
  for (let s = 0; s < visibleSections.length; s++) {
    const item = visibleSections[s].item;
    const index = visibleSections[s].index;
    const subs = permittedSubs(item);
    const isGroup = subs.length > 0;
    const sectionMatched = matches(item);

    if (!isGroup) {
      if (q === '' || sectionMatched) rows.push({ type: 'row', item, index });
      continue;
    }

    let shownSubs;
    if (q === '' || sectionMatched) {
      shownSubs = subs; // no query, or the section label itself matched → all subs
    } else {
      shownSubs = subs.filter(({ sub }) => matches(sub));
    }
    if (q !== '' && !sectionMatched && shownSubs.length === 0) continue; // nothing matched here

    const open = q !== '' ? true : openGroup === index; // searching auto-expands matches
    rows.push({ type: 'group', item, index, subs: shownSubs, open });
  }

  const fullName = loggedInUser ? `${loggedInUser.firstName} ${loggedInUser.lastName}` : '';
  const initials = loggedInUser
    ? `${(loggedInUser.firstName || ' ')[0]}${(loggedInUser.lastName || ' ')[0]}`.trim().toUpperCase()
    : '';

  return (
    <>
      <div className='sb-brand-row'>
        <button
          type='button'
          className='brand'
          onClick={goHome}
          aria-label='Home'
          title='Home'
        >
          <img
            src='/assets/img/brand/bare-icon.png'
            alt='Bravo'
          />
        </button>
        <span className='sb-wordmark'>BRAVO</span>
        <button
          type='button'
          className='sb-hdr-collapse'
          onClick={onCollapse}
          title='Collapse sidebar'
          aria-label='Collapse sidebar'
        >
          <i className='fa-solid fa-angle-left' />
        </button>
      </div>

      <div className='sb-search'>
        <i className='fa-solid fa-magnifying-glass' />
        <input
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search menu...'
          aria-label='Search menu'
        />
        {query && (
          <button
            type='button'
            className='sb-search-x'
            onClick={() => setQuery('')}
            aria-label='Clear'
          >
            <i className='fa-solid fa-xmark' />
          </button>
        )}
      </div>

      <div className='sb-list'>
        {rows.length === 0 && <div className='sb-empty'>No matches</div>}
        {rows.map((r) => {
          const active = r.index === activeButtons.nav;
          if (r.type === 'row') {
            return (
              <button
                type='button'
                key={`sb-row-${r.index}`}
                className={`sb-row${active ? ' active' : ''}`}
                onClick={() => goSection(r.item, r.index)}
                title={r.item.text}
              >
                <i className={r.item.icon} />
                <span className='lb'>{r.item.text}</span>
              </button>
            );
          }
          return (
            <div
              className='sb-group'
              key={`sb-group-${r.index}`}
            >
              <button
                type='button'
                className={`sb-row toggle${active ? ' active' : ''}`}
                aria-expanded={r.open}
                onClick={() => openAndNavigate(r.item, r.index)}
              >
                <i className={r.item.icon} />
                <span className='lb'>{r.item.text}</span>
                <span
                  className='chev-hit'
                  role='button'
                  tabIndex={-1}
                  aria-label={r.open ? 'Collapse section' : 'Expand section'}
                  onClick={(e) => {
                    // Clicking the arrow only toggles the sub-menu — it never navigates
                    // (unlike clicking the row label/icon, which opens + navigates).
                    e.stopPropagation();
                    setOpenGroup(openGroup === r.index ? null : r.index);
                  }}
                >
                  <i className={`fa-solid fa-chevron-down chev${r.open ? ' open' : ''}`} />
                </span>
              </button>
              {r.open && (
                <div className='sb-sub'>
                  {r.subs.map(({ sub, i }) => {
                    const subActive = active && i === activeButtons.sub;
                    return (
                      <button
                        type='button'
                        key={`sb-subrow-${r.index}-${i}`}
                        className={`sb-subrow${subActive ? ' active' : ''}`}
                        onClick={() => goSub(r.item, r.index, sub, i)}
                      >
                        {iconsEnabled() && <i className={sub.icon} />}
                        <span>{label(sub)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className='sb-foot'>
        <button
          type='button'
          className={`sb-user${profileActive ? ' active' : ''}`}
          onClick={onProfileToggle}
        >
          <span className='av'>{initials}</span>
          <span className='who'>
            <span className='nm'>{fullName}</span>
            <span className='rl'>{loggedInUser ? loggedInUser.userName : ''}</span>
          </span>
          <i className='fa-solid fa-user' />
        </button>
        <button
          type='button'
          className='sb-collapse'
          onClick={onCollapse}
        >
          <i className='fa-solid fa-angle-left' /> Collapse sidebar
        </button>
      </div>
    </>
  );
}
